import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import api from "../utils/api";
import { openRegistrationForm } from "../utils/registrationForm";

const STEPS = [
  { n: 1, label: "About you" },
  { n: 2, label: "Links & letter" },
];

export default function ApplyModal({
  job,
  user,
  onClose,
  notify,
  onSubmitted,
  onStartInterview,
  onUserUpdate,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    phone: "",
    experience: "",
    linkedin: "",
    portfolio: "",
    coverLetter: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);

  // The applicant can correct their name here — saving renames the account too.
  const [name, setName] = useState(user.name || "");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.name || "");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  // Post-submit flow: "success" → ask about more internships → interview
  const [stage, setStage] = useState("success"); // success | more | moreDone
  const [internJobs, setInternJobs] = useState([]);
  const [appliedIds, setAppliedIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [moreLoading, setMoreLoading] = useState(false);
  const [startingInterview, setStartingInterview] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Only internships go through the chat-interview flow. Lecturer and other
  // (senior/full-time) roles just get a plain submitted confirmation.
  const isInternship = /intern/i.test(job.title || "");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const apply = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/applications", {
        jobId: job._id || job.id,
        ...form,
      });
      onSubmitted?.(data);
      setSubmitted(true);
      notify("Application submitted successfully!");
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to submit application.";
      if (msg.includes("already applied")) {
        notify(msg, "error");
        onClose();
      } else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const startEditName = () => {
    setNameDraft(name);
    setNameError("");
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
    setNameError("");
  };

  // Saves to the account itself, so the new name is used everywhere — not just
  // on this application.
  const saveName = async () => {
    const next = nameDraft.trim();
    if (!next) {
      setNameError("Name cannot be empty.");
      return false;
    }
    if (next === name) {
      cancelEditName();
      return true;
    }
    setNameSaving(true);
    setNameError("");
    try {
      const { data } = await api.patch("/users/me", { name: next });
      setName(data.name || next);
      onUserUpdate?.(data);
      setEditingName(false);
      notify("Name updated on your account.");
      return true;
    } catch (err) {
      setNameError(
        err.response?.data?.message || "Could not update your name.",
      );
      return false;
    } finally {
      setNameSaving(false);
    }
  };

  // Don't let a half-finished rename fall through the cracks on Continue.
  const continueToStep2 = async () => {
    if (editingName && !(await saveName())) return;
    setStep(2);
  };

  // Move the candidate to the Quantum Team chat and kick off the AI interview.
  const startInterview = async () => {
    setStartingInterview(true);
    try {
      await api.post("/interview/start");
    } catch {
      // Bot unavailable — the chat still works, the team follows up manually.
    }
    setStartingInterview(false);
    if (onStartInterview) onStartInterview();
    else navigate("/dashboard?tab=messages");
    onClose();
  };

  const openMoreInternships = async () => {
    setMoreLoading(true);
    setStage("more");
    try {
      const [jobsRes, appsRes] = await Promise.all([
        api.get("/jobs"),
        api.get("/applications/mine"),
      ]);
      const interns = jobsRes.data.filter(
        (j) => j.active && /intern/i.test(j.title),
      );
      const applied = appsRes.data
        .filter((a) => a.status !== "rejected")
        .map((a) => String(a.jobId));
      setInternJobs(interns);
      setAppliedIds(applied);
      setSelectedIds(
        interns.map((j) => String(j._id)).filter((id) => applied.includes(id)),
      );
    } catch {
      notify("Could not load internships.", "error");
      setStage("success");
    } finally {
      setMoreLoading(false);
    }
  };

  const toggleJob = (id) => {
    if (appliedIds.includes(id)) return; // already applied — locked in
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const applyToSelected = async () => {
    const newIds = selectedIds.filter((id) => !appliedIds.includes(id));
    if (newIds.length === 0) {
      setStage("moreDone");
      return;
    }
    setMoreLoading(true);
    let applied = 0;
    for (const id of newIds) {
      try {
        const { data } = await api.post("/applications", { jobId: id, ...form });
        onSubmitted?.(data);
        applied++;
      } catch (err) {
        notify(
          err.response?.data?.message || "One application failed.",
          "error",
        );
      }
    }
    setMoreLoading(false);
    if (applied > 0)
      notify(
        `Applied to ${applied} more internship${applied > 1 ? "s" : ""}!`,
      );
    setStage("moreDone");
  };

  // Registration form — always built from every application on record, so a
  // candidate who applied for several roles gets all of them on one sheet.
  const downloadRegistrationForm = async () => {
    setFormLoading(true);
    try {
      const count = await openRegistrationForm({ ...user, name });
      notify(
        count > 1
          ? `Registration form downloaded — all ${count} positions included.`
          : "Registration form downloaded.",
      );
    } catch {
      notify("Could not generate the registration form.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const RegistrationFormButton = ({ style }) => (
    <button
      className="btn btn-ghost"
      style={{
        width: "100%",
        marginTop: "0.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        ...style,
      }}
      onClick={downloadRegistrationForm}
      disabled={formLoading}
    >
      {formLoading ? (
        <span className="auth-spinner" />
      ) : (
        <>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download Registration Form (PDF)
        </>
      )}
    </button>
  );

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="apply-modal">
        <button className="modal-close" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>

        {submitted ? (
          <div className="apply-success">
            {!isInternship ? (
              <>
                <div className="apply-success-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="apply-success-title">Application Submitted!</h2>
                <p className="apply-success-sub">
                  We've received your application for{" "}
                  <strong>{job.title}</strong>. Our team will review it and
                  reach out if there's a match.
                </p>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%" }}
                  onClick={onClose}
                >
                  Done
                </button>
                <RegistrationFormButton />
              </>
            ) : (
              <>
                {stage === "success" && (
                  <>
                    <div className="apply-success-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <h2 className="apply-success-title">Application Sent!</h2>
                    <p className="apply-success-sub">
                      We've received your application for{" "}
                      <strong>{job.title}</strong>. Your chat interview with
                      the Quantum Team is ready whenever you are.
                    </p>
                    <p
                      style={{
                        fontSize: ".95rem",
                        fontWeight: 700,
                        margin: "0 0 0.875rem",
                      }}
                    >
                      Would you like to apply for another internship?
                    </p>
                    <div style={{ display: "flex", gap: "0.75rem" }}>
                      <button
                        className="btn btn-ghost"
                        style={{ flex: 1 }}
                        onClick={openMoreInternships}
                      >
                        Yes, show me
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1.4 }}
                        onClick={startInterview}
                        disabled={startingInterview}
                      >
                        {startingInterview ? (
                          <span className="auth-spinner" />
                        ) : (
                          "No — start my interview"
                        )}
                      </button>
                    </div>
                    <RegistrationFormButton />
                  </>
                )}

                {stage === "more" && (
                  <>
                    <h2
                      className="apply-success-title"
                      style={{ marginTop: 0 }}
                    >
                      Apply to more internships
                    </h2>
                    <p className="apply-success-sub">
                      Your details from this application will be reused.
                      Roles you've already applied for are locked in.
                    </p>
                    {moreLoading && internJobs.length === 0 ? (
                      <div style={{ padding: "1.5rem 0", textAlign: "center" }}>
                        <span className="auth-spinner" />
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          margin: "0.25rem 0 1rem",
                          textAlign: "left",
                        }}
                      >
                        {internJobs.map((j) => {
                          const id = String(j._id);
                          const isApplied = appliedIds.includes(id);
                          const isChecked = selectedIds.includes(id);
                          return (
                            <label
                              key={id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "11px 14px",
                                borderRadius: 10,
                                border: `1px solid ${
                                  isChecked
                                    ? "rgba(79,142,247,.45)"
                                    : "var(--line-2, rgba(255,255,255,.08))"
                                }`,
                                background: isChecked
                                  ? "rgba(79,142,247,.08)"
                                  : "transparent",
                                cursor: isApplied ? "default" : "pointer",
                                opacity: isApplied ? 0.75 : 1,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isApplied}
                                onChange={() => toggleJob(id)}
                                style={{ accentColor: "#4f8ef7" }}
                              />
                              <span
                                style={{
                                  fontSize: ".875rem",
                                  fontWeight: 600,
                                }}
                              >
                                {j.title}
                              </span>
                              <span
                                style={{
                                  marginLeft: "auto",
                                  fontSize: ".7rem",
                                  fontWeight: 700,
                                  color: isApplied
                                    ? "var(--green, #22c55e)"
                                    : "var(--text-3, #94a3b8)",
                                }}
                              >
                                {isApplied ? "✓ Applied" : j.department}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "0.75rem" }}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setStage("moreDone")}
                        disabled={moreLoading}
                      >
                        Skip
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        onClick={applyToSelected}
                        disabled={moreLoading}
                      >
                        {moreLoading ? (
                          <span className="auth-spinner" />
                        ) : (
                          "Apply to selected"
                        )}
                      </button>
                    </div>
                  </>
                )}

                {stage === "moreDone" && (
                  <>
                    <div className="apply-success-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <h2 className="apply-success-title">You're all set!</h2>
                    <p className="apply-success-sub">
                      Next up: a quick chat interview with the Quantum Team,
                      right here on the site.
                    </p>
                    <button
                      className="btn btn-primary"
                      style={{ width: "100%" }}
                      onClick={startInterview}
                      disabled={startingInterview}
                    >
                      {startingInterview ? (
                        <span className="auth-spinner" />
                      ) : (
                        "Start my interview →"
                      )}
                    </button>
                    <RegistrationFormButton />
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="apply-modal-header">
              <div className="apply-modal-job-info">
                <span className="apply-modal-dept">{job.department}</span>
                <h2 className="apply-modal-title">{job.title}</h2>
                <div className="apply-modal-meta">
                  <span>{job.location}</span>
                  <span className="apply-modal-meta-dot">·</span>
                  <span>{job.type}</span>
                  {job.salary && (
                    <>
                      <span className="apply-modal-meta-dot">·</span>
                      <span>{job.salary}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="apply-modal-divider" />

            {/* Progress — completed steps stay clickable so you can go back. */}
            <div className="apply-progress">
              {STEPS.map(({ n, label }, i) => {
                const state = step > n ? "done" : step === n ? "active" : "todo";
                return (
                  <Fragment key={n}>
                    {i > 0 && (
                      <span className="apply-progress-track">
                        <span
                          className="apply-progress-fill"
                          style={{ width: step >= n ? "100%" : "0%" }}
                        />
                      </span>
                    )}
                    <div className={`apply-progress-step is-${state}`}>
                      <button
                        type="button"
                        className="apply-progress-node"
                        onClick={() => state === "done" && setStep(n)}
                        disabled={state !== "done"}
                        aria-current={state === "active" ? "step" : undefined}
                        aria-label={`Step ${n} — ${label}`}
                      >
                        {state === "done" ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          n
                        )}
                      </button>
                      <span className="apply-progress-label">{label}</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            {/* Step 1 */}
            {step === 1 && (
              <div className="apply-form-step">
                <div className="apply-readonly-row">
                  <div className="apply-readonly-item">
                    <span className="apply-readonly-label">Name</span>
                    {editingName ? (
                      <>
                        <div className="apply-name-edit">
                          <input
                            className="form-input apply-name-input"
                            value={nameDraft}
                            autoFocus
                            disabled={nameSaving}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveName();
                              if (e.key === "Escape") cancelEditName();
                            }}
                          />
                          <button
                            type="button"
                            className="apply-name-btn apply-name-btn--save"
                            onClick={saveName}
                            disabled={nameSaving}
                            title="Save name"
                          >
                            {nameSaving ? (
                              <span className="auth-spinner" />
                            ) : (
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className="apply-name-btn"
                            onClick={cancelEditName}
                            disabled={nameSaving}
                            title="Cancel"
                          >
                            <Icon name="x" size={12} />
                          </button>
                        </div>
                        {nameError && (
                          <span className="apply-name-error">{nameError}</span>
                        )}
                      </>
                    ) : (
                      <div className="apply-name-view">
                        <span className="apply-readonly-value">{name}</span>
                        <button
                          type="button"
                          className="apply-name-edit-link"
                          onClick={startEditName}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                          >
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="apply-readonly-item">
                    <span className="apply-readonly-label">Email</span>
                    <span className="apply-readonly-value">{user.email}</span>
                  </div>
                </div>
                <div className="apply-form-grid">
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      className="form-input"
                      placeholder="+92 300 0000000"
                      value={form.phone}
                      onChange={set("phone")}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Years of Experience</label>
                    <select
                      className="form-select"
                      value={form.experience}
                      onChange={set("experience")}
                    >
                      <option value="">Select experience</option>
                      <option>0–1 years</option>
                      <option>1–3 years</option>
                      <option>3–5 years</option>
                      <option>5+ years</option>
                    </select>
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%", marginTop: "0.5rem" }}
                  onClick={continueToStep2}
                  disabled={nameSaving}
                >
                  Continue
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="apply-form-step">
                <div className="apply-form-grid">
                  <div className="form-group">
                    <label className="form-label">LinkedIn Profile</label>
                    <div className="form-input-icon-wrap">
                      <svg
                        className="form-input-icon"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                      </svg>
                      <input
                        className="form-input form-input-with-icon"
                        placeholder="linkedin.com/in/yourname"
                        value={form.linkedin}
                        onChange={set("linkedin")}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Portfolio / GitHub</label>
                    <div className="form-input-icon-wrap">
                      <svg
                        className="form-input-icon"
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                      </svg>
                      <input
                        className="form-input form-input-with-icon"
                        placeholder="github.com/yourname"
                        value={form.portfolio}
                        onChange={set("portfolio")}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Cover Letter{" "}
                    <span
                      style={{
                        color: "var(--text-3)",
                        fontWeight: 400,
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      (optional)
                    </span>
                  </label>
                  <textarea
                    className="form-textarea custom-placeholder"
                    style={{ minHeight: 120 }}
                    placeholder="Tell us why you're a great fit and what excites you about this role…"
                    value={form.coverLetter}
                    onChange={set("coverLetter")}
                  />
                </div>
                {error && (
                  <div
                    className="auth-error"
                    style={{ marginBottom: "0.75rem" }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </div>
                )}
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button className="btn btn-ghost" onClick={() => setStep(1)}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    onClick={apply}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="auth-spinner" />
                    ) : (
                      "Submit Application"
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
